import type {
  PublicSpeakerSignupCreateBody,
  PublicSpeakerSignupCreateResponse,
  PublicSpeakerSignupMeeting,
  PublicSpeakerSignupOptionsResponse,
  PublicSpeakerUserSignupsResponse,
} from '@govinity/validation/public';

import { ReadOnlyApi } from '../base';

/**
 * Client for the pre-meeting public speaker sign-up flow: the meetings currently open for sign-up
 * (`getAllPaginated`), the agenda entries a citizen may speak on for one of them (`getById`), and
 * the sign-up itself. Every route 403s when the government has public speakers switched off, which
 * the portal page surfaces as an "unavailable" state.
 */
export class PublicSpeakersApi extends ReadOnlyApi<
  PublicSpeakerSignupMeeting,
  PublicSpeakerSignupOptionsResponse
> {
  parentPath: string;
  governmentCode: string;

  constructor(governmentCode: string, parentPath: string) {
    super();

    this.parentPath = parentPath;
    this.governmentCode = governmentCode;
  }

  getPath() {
    return `${this.parentPath}/${this.governmentCode}/meetings/public-speakers`;
  }

  async signUp(meetingId: number, body: PublicSpeakerSignupCreateBody) {
    const { data } = await this.request<PublicSpeakerSignupCreateResponse>({
      method: 'POST',
      url: `${this.getPath()}/${meetingId}`,
      data: body,
    });

    return data;
  }

  /**
   * The signed-in citizen's own sign-ups on a meeting. Only these carry an owner, so they are the
   * only ones that can be listed back or withdrawn — an anonymous {@link signUp} cannot.
   */
  async getUserSignups(meetingId: number) {
    const { data } = await this.request<PublicSpeakerUserSignupsResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/user`,
    });

    return data;
  }

  async signUpAsUser(meetingId: number, body: PublicSpeakerSignupCreateBody) {
    const { data } = await this.request<PublicSpeakerSignupCreateResponse>({
      method: 'POST',
      url: `${this.getPath()}/${meetingId}/user`,
      data: body,
    });

    return data;
  }

  async withdrawUserSignup(meetingId: number, speakerId: number) {
    await this.request({
      method: 'DELETE',
      url: `${this.getPath()}/${meetingId}/user/${speakerId}`,
    });
  }
}
